import { createContext, useContext, useState, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import type { PatientAuthContext } from '../api/patientTypes';
import { getAuthContext, setAuthContext, clearAuthContext } from '../api/http';
import { patientAuthFlow } from '../modules/patient/auth/patientAuthFlow';

interface AuthState {
  user: PatientAuthContext | null;
  isAuthenticated: boolean;
  login: (ctx: PatientAuthContext) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PatientAuthContext | null>(() => getAuthContext());

  // patientAuthFlow's backendLogin/backendVerifyOtp/backendRegister write the
  // session via the standalone setAuthContext() in api/http.ts, not through
  // this provider's own `login()` — so without this subscription, a
  // successful login updates localStorage but this component's `user` state
  // (and anything gated on isAuthenticated, e.g. OnboardingGate) never finds
  // out, and the router bounces the user straight back to the entry screen.
  useEffect(() => {
    return patientAuthFlow.subscribe(() => {
      setUser(getAuthContext());
    });
  }, []);

  const login = useCallback((ctx: PatientAuthContext) => {
    setAuthContext(ctx);
    setUser(ctx);
  }, []);

  const logout = useCallback(() => {
    clearAuthContext();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    isAuthenticated: user !== null,
    login,
    logout,
  }), [user, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export { AuthContext };
export type { AuthState };
